import { useApolloClient } from '@apollo/client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { retainedBeaconDischargeEligibility } from '@/graph/patients';

export const RETAINED_BEACON_ELIGIBILITY_TIMEOUT_MS = 5000;

export type RetainedBeaconChoice = 'standard' | 'retain';
export type RetainedBeaconEligibilityStatus = 'idle' | 'loading' | 'ineligible' | 'eligible';

export interface RetainedBeaconEligibility {
  eligible: boolean;
  reason: string;
  procedureId?: number | null;
  scheduledStart?: string | null;
  orId?: number | null;
  expiresAt?: string | null;
}

export function canSubmitRetainedBeaconDischarge(
  status: RetainedBeaconEligibilityStatus,
  choice: RetainedBeaconChoice | null
) {
  return status !== 'eligible' || choice !== null;
}

export function shouldKeepBeacon(status: RetainedBeaconEligibilityStatus, choice: RetainedBeaconChoice | null) {
  return status === 'eligible' && choice === 'retain';
}

interface Options {
  patientId?: number | string | null;
  enabled: boolean;
  timeoutMs?: number;
  surface?: string;
}

class EligibilityTimeoutError extends Error {}

export function useRetainedBeaconDischarge({
  patientId,
  enabled,
  timeoutMs = RETAINED_BEACON_ELIGIBILITY_TIMEOUT_MS,
  surface = 'unknown',
}: Options) {
  const client = useApolloClient();
  const requestRef = useRef(0);
  const startedAtRef = useRef<number | null>(null);
  const [status, setStatus] = useState<RetainedBeaconEligibilityStatus>('idle');
  const [eligibility, setEligibility] = useState<RetainedBeaconEligibility | null>(null);
  const [choice, setChoice] = useState<RetainedBeaconChoice | null>(null);
  const [degradationReason, setDegradationReason] = useState<'Error' | 'Timeout' | null>(null);

  const reset = useCallback(() => {
    requestRef.current += 1;
    setStatus('idle');
    setEligibility(null);
    setChoice(null);
    setDegradationReason(null);
    startedAtRef.current = null;
  }, []);

  useEffect(
    () => () => {
      requestRef.current += 1;
    },
    []
  );

  const begin = useCallback(() => {
    reset();
    if (!enabled || patientId == null) return;

    const request = requestRef.current;
    startedAtRef.current = Date.now();
    setStatus('loading');

    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_, reject) => {
      timeoutHandle = setTimeout(
        () => reject(new EligibilityTimeoutError('Retained beacon eligibility request timed out')),
        timeoutMs
      );
    });

    void Promise.race([
      client.query<{ retainedBeaconDischargeEligibility: RetainedBeaconEligibility }>({
        query: retainedBeaconDischargeEligibility,
        variables: { id: Number(patientId) },
        fetchPolicy: 'network-only',
      }),
      timeout,
    ])
      .then(result => {
        if (request !== requestRef.current) return;
        const next = result.data.retainedBeaconDischargeEligibility;
        setEligibility(next);
        setStatus(next.eligible ? 'eligible' : 'ineligible');
      })
      .catch(error => {
        if (request !== requestRef.current) return;
        const reason = error instanceof EligibilityTimeoutError ? 'Timeout' : 'Error';
        setDegradationReason(reason);
        console.warn('[RetainedBeacon] EligibilityDegraded', {
          patientId,
          reason,
          elapsedMs: startedAtRef.current == null ? undefined : Date.now() - startedAtRef.current,
          surface,
          error: error instanceof Error ? error.message : String(error),
        });
        setStatus('ineligible');
      })
      .finally(() => {
        if (timeoutHandle) clearTimeout(timeoutHandle);
      });
  }, [client, enabled, patientId, reset, surface, timeoutMs]);

  const prepareSubmission = useCallback(() => {
    if (status === 'loading') {
      requestRef.current += 1;
      setStatus('ineligible');
      console.info('[RetainedBeacon] SubmittedBeforeResolved', {
        patientId,
        elapsedMs: startedAtRef.current == null ? undefined : Date.now() - startedAtRef.current,
        surface,
      });
      return false;
    }

    return shouldKeepBeacon(status, choice);
  }, [choice, patientId, status, surface]);

  return {
    status,
    eligibility,
    degradationReason,
    choice,
    setChoice,
    begin,
    reset,
    prepareSubmission,
    requiresChoice: status === 'eligible',
    canSubmit: canSubmitRetainedBeaconDischarge(status, choice),
  };
}
